import { useState } from 'react';

interface Booking {
  id: string;
  guest_display_name: string;
  booking_channel: string;
  source_override: string | null;
  rooms: { name: string } | null;
}

interface OverrideSourceModalProps {
  booking: Booking;
  onClose: () => void;
  onSubmit: (bookingId: string, source: string, reason: string) => Promise<boolean>;
}

const SOURCE_OPTIONS = [
  { value: 'direct', label: '直接予約' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking', label: 'Booking.com' },
  { value: 'trip', label: 'Trip.com' },
  { value: 'expedia', label: 'Expedia' },
  { value: 'phone', label: '電話' },
  { value: 'walk_in', label: '飛び込み' },
  { value: 'other', label: 'その他' },
];

export default function OverrideSourceModal({ booking, onClose, onSubmit }: OverrideSourceModalProps) {
  const [source, setSource] = useState(booking.source_override || booking.booking_channel || 'direct');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('訂正理由を入力してください。');
      return;
    }
    setError(null);
    setLoading(true);
    const success = await onSubmit(booking.id, source, reason.trim());
    setLoading(false);
    if (success) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-background-50 rounded-xl shadow-lg max-w-md w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-900">予約経路の訂正</h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="text-xs text-foreground-500 bg-background-100 rounded-lg px-4 py-3">
            {booking.guest_display_name} · {booking.rooms?.name || '—'}
          </div>

          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">正しい予約経路</label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">訂正理由 <span className="text-red-500">*</span></label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="例: 実際は電話予約だった"
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
              required
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin"></i>
                </span>
              )}
              訂正する
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}